import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Dialog, DialogContent, Grow } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import { ThemeScope, Button, Input, Label, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, Separator } from "./ui/shadcn";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

function LoginDesign03({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#1877F2" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  const reduce = useReducedMotion();
  const INK = "#0a0a0a";
  const MUTED = "#737373";
  const HAIR = "#e5e5e5";

  const themeVars = {
    "--background": "#ffffff",
    "--foreground": INK,
    "--card": "#ffffff",
    "--card-foreground": INK,
    "--primary": INK,
    "--primary-foreground": "#fafafa",
    "--secondary": "#f5f5f4",
    "--secondary-foreground": INK,
    "--muted": "#f5f5f4",
    "--muted-foreground": MUTED,
    "--accent": "#f5f5f4",
    "--destructive": "#b91c1c",
    "--destructive-foreground": "#ffffff",
    "--border": HAIR,
    "--input": HAIR,
    "--ring": INK,
    "--radius": "0.375rem",
    fontFamily: "'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif",
  };

  const EASE = [0.22, 1, 0.36, 1];
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.15 } },
  };
  const item = {
    hidden: reduce ? { opacity: 1 } : { opacity: 1, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: reduce ? 0.01 : 0.6, ease: EASE } },
  };

  const capsHelper = (
    <div style={{ minHeight: 18, marginTop: 4, fontSize: 12, color: "#b45309", display: "flex", alignItems: "center", gap: 4 }}>
      {capsLockOn && (<><WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON</>)}
    </div>
  );

  const errorAlert = (
    <AnimatePresence>
      {errorMessage && (
        <motion.div
          key={errorMessage}
          initial={{ opacity: 1, y: reduce ? 0 : -6 }}
          animate={reduce ? { opacity: 1, y: 0 } : { opacity: 1, y: 0, x: [0, -6, 6, -4, 4, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.01 : 0.45 }}
          style={{ marginBottom: 20 }}
        >
          <Alert variant="destructive" style={{ display: "flex", alignItems: "center", gap: 8, borderRadius: 2, borderWidth: "0 0 0 2px", background: "#fef2f2" }}>
            <WarningAmberIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const btnMotion = reduce ? {} : { whileHover: { scale: 1.01 }, whileTap: { scale: 0.98 } };

  return (
    <ThemeScope vars={themeVars} className="login-root l3-page" style={{ minHeight: "100vh", width: "100%", maxWidth: "100vw", overflowX: "hidden", display: "flex", flexDirection: "column", alignItems: "center", background: "#fafaf8", padding: "clamp(16px, 4vh, 40px) clamp(16px, 4vw, 32px)" }}>
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".login-root, .login-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif; }",
        ".login-root .l3-input { border: 0; border-bottom: 1px solid #a3a3a3; border-radius: 0; padding: 10px 0; height: 44px; font-size: 0.98rem; background: #fff; color: " + INK + "; -webkit-text-fill-color: " + INK + "; transition: border-color .2s ease; }",
        ".login-root .l3-input:hover { border-bottom-color: " + INK + "; }",
        ".login-root .l3-input:-webkit-autofill, .login-root .l3-input:-webkit-autofill:hover, .login-root .l3-input:-webkit-autofill:focus { -webkit-text-fill-color: " + INK + "; -webkit-box-shadow: 0 0 0 1000px #fff inset; }",
        ".login-root .l3-input:focus, .login-root .l3-input:focus-visible { box-shadow: none; border-bottom: 1.5px solid " + INK + "; margin-bottom: -0.5px; }",
        ".login-root .l3-label { color: " + MUTED + "; font-weight: 400; font-size: 0.8rem; letter-spacing: .3px; margin-bottom: 2px; }",
        ".login-root .l3-primary { width: 100%; height: 46px; border-radius: 2px; text-transform: uppercase; letter-spacing: 1.6px; font-size: .82rem; }",
        ".login-root .l3-option { width: 100%; text-align: left; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; border-radius: 2px; background: #fff; border: 1px solid " + HAIR + "; cursor: pointer; font: inherit; color: " + INK + "; transition: border-color .2s ease; }",
        ".login-root .l3-option:hover { border-color: " + INK + "; }",
        ".login-root .l3-option:focus-visible { outline: 2px solid " + INK + "; outline-offset: 2px; }",
        ".login-root .l3-option[aria-checked='true'] { border-color: " + INK + "; box-shadow: inset 2px 0 0 " + INK + "; }",
        ".login-root.l3-page { min-height: 100dvh !important; }",
        ".login-root .l3-primary, .login-root .ui-btn { min-height: 44px; }",
        ".login-root .l3-option { min-height: 48px; min-width: 0; }",
        ".login-root .l3-option span { min-width: 0; overflow-wrap: anywhere; word-break: break-word; }",
        ".login-root .l3-card h1 { font-size: clamp(1.3rem, 4.5vw, 1.55rem); }",
        "@media (max-height: 480px) { .login-root .l3-logo { margin-bottom: 16px !important; } .login-root .l3-head { padding-top: 20px !important; padding-bottom: 12px !important; } .login-root .l3-foot { margin-top: 16px !important; } }",
        ".login-root a:focus-visible { outline: 2px solid " + INK + "; outline-offset: 3px; }",
      ].join("\n")}</style>

      <main style={{ width: "100%", maxWidth: "clamp(300px, 92vw, 400px)", minWidth: 0, margin: "auto 0" }}>
        <motion.div
          initial={{ opacity: 1, y: reduce ? 0 : 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0.01 : 0.6, ease: EASE }}
          className="l3-logo" style={{ display: "flex", justifyContent: "center", marginBottom: "clamp(20px, 5vh, 40px)" }}
        >
          <img src="/RBLOGOBLUE.svg" alt="ReonBook" style={{ height: 34, filter: "grayscale(1) brightness(0)" }} />
        </motion.div>

        <motion.div
          initial={{ opacity: 1, y: reduce ? 0 : 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0.01 : 0.7, ease: EASE, delay: reduce ? 0 : 0.08 }}
        >
          <Card className="l3-card" style={{ position: "relative", boxShadow: "none", overflow: "hidden", minWidth: 0 }}>
            <motion.div
              aria-hidden="true"
              initial={{ scaleX: reduce ? 1 : 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: reduce ? 0.01 : 0.9, ease: EASE, delay: reduce ? 0 : 0.25 }}
              style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: INK, transformOrigin: "left", pointerEvents: "none", zIndex: 0 }}
            />

            <AnimatePresence mode="wait" initial={false}>
              {!isCompanySelectionVisible ? (
                <motion.div
                  key="login"
                  variants={container}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: reduce ? 0 : -8, transition: { duration: 0.2 } }}
                >
                  <CardHeader className="l3-head" style={{ padding: "40px min(40px, 8vw) 24px" }}>
                    <motion.div variants={item}>
                      <CardTitle style={{ fontWeight: 500, fontSize: "1.55rem" }}>
                        <h1 style={{ margin: 0, font: "inherit", letterSpacing: "inherit" }}>Sign in</h1>
                      </CardTitle>
                    </motion.div>
                    <motion.div variants={item}>
                      <CardDescription style={{ fontWeight: 300 }}>to your ReonBook workspace</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent style={{ padding: "0 min(40px, 8vw) 12px" }}>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>
                      <div style={{ marginBottom: 8 }}>
                        <Label htmlFor="login03-identifier" className="l3-label">Username, Email or Mobile</Label>
                        <Input id="login03-identifier" className="l3-input" type="text" value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          autoComplete="username" />
                        {capsHelper}
                      </div>

                      <div style={{ marginBottom: 8 }}>
                        <Label htmlFor="login03-password" className="l3-label">Password</Label>
                        <div style={{ position: "relative" }}>
                          <Input id="login03-password" className="l3-input" style={{ paddingRight: 40 }}
                            type={showPassword ? "text" : "password"} value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                            autoComplete="current-password" />
                          <Button type="button" variant="ghost" onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            style={{ position: "absolute", right: -6, top: 0, height: 44, width: 44, padding: 0, color: MUTED }}>
                            {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </div>

                      <motion.div variants={item} style={{ display: "flex", justifyContent: "flex-end", marginBottom: 28 }}>
                        <Link to="/forgot-password" style={{ color: MUTED, fontSize: "0.8rem", textDecoration: "underline", textUnderlineOffset: 3 }}>
                          Forgot password?
                        </Link>
                      </motion.div>

                      {errorAlert}

                      <motion.div variants={item}>
                        <motion.div {...btnMotion}>
                          <Button type="submit" disabled={isLoading} className="l3-primary">
                            {isLoading
                              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />Signing in...</span>
                              : "Sign In"}
                          </Button>
                        </motion.div>
                      </motion.div>

                      {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                        <motion.div variants={item} style={{ marginTop: 12 }}>
                          <Button type="button" variant="outline" style={{ width: "100%", minHeight: 44 }}
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        </motion.div>
                      )}
                    </form>
                  </CardContent>

                  <motion.div variants={item} style={{ padding: "0 min(40px, 8vw)" }}>
                    <Separator style={{ margin: "20px 0" }} />
                  </motion.div>

                  <CardFooter style={{ padding: "0 min(40px, 8vw) 36px", justifyContent: "center" }}>
                    <motion.p variants={item} style={{ margin: 0, color: MUTED, fontSize: "0.85rem", textAlign: "center" }}>
                      Don't have an account?{" "}
                      <Link to="/register" style={{ color: INK, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}>
                        Sign up
                      </Link>
                    </motion.p>
                  </CardFooter>
                </motion.div>
              ) : (
                <motion.div
                  key="company"
                  variants={container}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0 }}
                >
                  <CardHeader className="l3-head" style={{ padding: "40px min(40px, 8vw) 24px" }}>
                    <motion.div variants={item}>
                      <CardTitle style={{ fontWeight: 500, fontSize: "1.55rem" }}>
                        <h1 style={{ margin: 0, font: "inherit", letterSpacing: "inherit" }}>Select organization</h1>
                      </CardTitle>
                    </motion.div>
                    <motion.div variants={item}>
                      <CardDescription style={{ fontWeight: 300 }}>Choose which organization to access</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent style={{ padding: "0 min(40px, 8vw) 36px" }}>
                    <div role="radiogroup" aria-label="Choose your organization" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24, maxHeight: 280, overflowY: "auto" }}>
                      {companies.map((c) => {
                        const checked = selectedCompany?.companyId === c.companyId;
                        return (
                          <motion.button
                            key={c.companyId}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            variants={item}
                            whileTap={reduce ? undefined : { scale: 0.99 }}
                            className="l3-option"
                            onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                          >
                            <span style={{ fontSize: "0.92rem", fontWeight: checked ? 500 : 400 }}>{c.companyName}</span>
                            {checked && <CheckIcon sx={{ fontSize: 16 }} />}
                          </motion.button>
                        );
                      })}
                    </div>

                    {errorAlert}

                    <motion.div variants={item}>
                      <motion.div {...btnMotion}>
                        <Button type="button" className="l3-primary" onClick={() => FN_SelectCompany()}>
                          Continue to Dashboard →
                        </Button>
                      </motion.div>
                    </motion.div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>

        <motion.p
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduce ? 0.01 : 0.8, delay: reduce ? 0 : 0.7 }}
          className="l3-foot" style={{ marginTop: 32, textAlign: "center", color: "#a3a3a3", fontSize: "0.72rem", letterSpacing: "0.3px" }}
        >
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </motion.p>
      </main>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={reduce ? 0 : { enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(2px)", bgcolor: "rgba(250,250,248,0.75)" } }}
        PaperProps={{ sx: { borderRadius: "6px", overflow: "hidden", border: "1px solid " + HAIR, boxShadow: "0 20px 50px rgba(0,0,0,0.08)", m: { xs: 1.5, sm: 2 }, maxHeight: "calc(100% - 24px)", width: "calc(100% - 24px)", maxWidth: 420 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={themeVars} className="login-root">
            <div style={{ padding: "clamp(16px, 5vw, 24px)", borderBottom: "1px solid " + HAIR }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <DevicesIcon sx={{ color: INK, fontSize: 22 }} />
                <div>
                  <Typography fontWeight={500} fontSize="1.05rem" color={INK} lineHeight={1.3}>
                    Session limit reached
                  </Typography>
                  <Typography fontSize="0.78rem" color={MUTED} mt={0.2}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </Typography>
                </div>
              </div>
              <Typography fontSize="0.82rem" color={MUTED} lineHeight={1.5}>
                Sign out of one of the devices below to log in on this device.
              </Typography>
            </div>

            <div style={{ padding: "16px clamp(14px, 4vw, 20px) 8px" }}>
              <Typography fontSize="0.7rem" fontWeight={500} color="#a3a3a3" letterSpacing="1.4px" textTransform="uppercase" mb={1.5}>
                Active sessions
              </Typography>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <motion.div key={session.id}
                  initial={{ opacity: 1, y: reduce ? 0 : 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduce ? 0.01 : 0.4, ease: EASE, delay: reduce ? 0 : 0.1 + idx * 0.07 }}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid " + HAIR }}>
                  <div style={{ width: 36, height: 36, border: "1px solid " + HAIR, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={500} fontSize="0.87rem" color={INK} noWrap>
                      {session.device_name || "Unknown Device"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: "#a3a3a3" }} />
                      <Typography fontSize="0.73rem" color={MUTED}>
                        {timeAgo(session.last_activity)}
                      </Typography>
                      {session.ip_address && (
                        <Typography fontSize="0.73rem" color="#a3a3a3" ml={0.5} noWrap>
                          · {session.ip_address}
                        </Typography>
                      )}
                    </Box>
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                    style={{ flexShrink: 0, minWidth: 80, minHeight: 44, borderColor: INK }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: INK }} />
                      : "Sign Out"}
                  </Button>
                </motion.div>
              ))}
            </div>

            <div style={{ padding: "12px clamp(14px, 4vw, 20px) 20px" }}>
              <Button variant="outline" style={{ width: "100%", minHeight: 44, color: MUTED }} onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>
    </ThemeScope>
  );
}

export default LoginDesign03;
